import axios, { AxiosResponse } from 'axios';
import { point, lineString, featureCollection, polygon } from '@turf/helpers';
import type { Feature, Point as GeoJSONPoint } from 'geojson';
import polygonToLine from '@turf/polygon-to-line';
import nearestPointOnLine from '@turf/nearest-point-on-line';
import distance from '@turf/distance';
import bearing from '@turf/bearing';
import destination from '@turf/destination';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import { gunzipSync } from 'fflate';
import { Feature as GeoJSONFeature, Polygon, MultiPolygon, FeatureCollection } from 'geojson';
import { Mission, Sample, Zone } from '../../db';
import {
  createBridgePolygonToBoundary,
  glueBoundaryWithBridgesAndUnsafeZones,
  isPointSafeInsidePolygon,
  bufferBoundaryPolygonsInward,
  expandPolygonOutward,
} from './utils';
import { NavigationPoint, NavigationPointType, PolyOrMultiPoly } from './types';
import { boundaryNotices, rememberSmartPathBridges, type SmartPathBridge } from './bridges';
import logger from '../../logger';
import { alertError, alertWarn } from '../../alertDispatcher';
import {
  SMART_PATH_BORDER_CLUSTER_PENALTY_FACTOR_DEFAULT,
  SMART_PATH_DGP_ITERATION_CAP_DEFAULT,
  SMART_PATH_GRID_CELL_SIZE_METERS_DEFAULT,
  SMART_PATH_HYBRID_ASTAR_NODE_CAP_DEFAULT,
  SMART_PATH_HYBRID_ASTAR_TIME_BUDGET_MS_DEFAULT,
  SMART_PATH_PREFERRED_ANGLE_DEGREES_DEFAULT,
  SMART_PATH_PREFERRED_ANGLE_PENALTY_FACTOR_DEFAULT,
  SMART_PATH_ZONE_CLUSTER_PENALTY_FACTOR_DEFAULT,
  SMART_PATH_MIN_DISTANCE_TO_BOUNDARY_METERS_DEFAULT,
  SMART_PATH_TOO_CLOSE_DISTANCE_TO_BOUNDARY_METERS,
  SMART_PATH_CRUISE_SPEED_MPS_DEFAULT,
  SMART_PATH_TURN_SPEED_FACTOR_DEFAULT,
  SMART_PATH_SHARP_TURN_WEIGHT_DEFAULT,
} from '../../constants';
import { dispatchMissionUpdated, MISSION_EVENTS } from '../../missionEvents';
import EventBus from '../../EventBus';
import { splitMultipartBody } from '../MultipartResponseParser';
import { deleteExtraCoresForSamples } from '../../db_ops/mission_ops';
import { extractPathfinderErrorDetail, pathfinderFindUrl, pathfinderHeaders } from './pathfinderApi';
import { implementsCenteredNavigation, pathfinderMissionType } from './pathfinderMission';
import { GeoDataUtilities } from '@rogoag/shared-ts';

type Route = Path[];
type Path = NavigationPoint[];

// 'off'              — no debug output. Pathfinder gets debugGeoJson=false.
// 'download'         — pathfinder returns a multipart body with a gzipped
//                      debug GeoJSON attachment that gets saved to disk.
// 'layer'            — same response shape as 'download' but rendered as a
//                      MapMaker layer instead of being saved.
// 'layer_with_trace' — same as 'layer' PLUS appends `?debug=1` to the URL,
//                      asking pathfinder to capture per-leg algorithm
//                      attempts + Hybrid A* sampled-node trace into the
//                      response's top-level `attempts[]` field.
export type DebugGeoJsonMode = 'off' | 'download' | 'layer' | 'layer_with_trace';

export type SmartPathBuildResult = {
  path?: NavigationPoint[];
  routingBoundary?: Feature<Polygon>;
  defectiveGeometries?: Feature<GeoJSON.Point | GeoJSON.Polygon>[];
  warningGeometries?: Feature<GeoJSON.Point | GeoJSON.Polygon>[];
  // Echoed from pathfinder response — post-hoc stats of the chosen tour.
  totalTimeSec?: number;
  totalPhysicalDistance?: number;
  totalBorderCrossings?: number;
  totalZoneCrossings?: number;
  costModel?: 'plain_atsp' | 'dubins_expanded';
  // Server wall time for the whole pathfinder request (ms). Distinct from
  // totalTimeSec, which is the robot's predicted drive time.
  requestWallTimeMs?: number;
};

const BORDER_CLUSTER_ID_DEFAULT = 1;
const BORDER_CLUSTER_ID_OUTSIDE = 'out';
const ZONE_CLUSTER_ID_DEFAULT = 'default_zone';

// Mirrors pathfinder's startPosition validation (geojson_parser.cpp), which
// rejects a 0 or non-finite coordinate with a 500.
const isUnsetPosition = (lat: number, lon: number): boolean =>
  !Number.isFinite(lat) || !Number.isFinite(lon) || lat === 0 || lon === 0;

// localStorage keys used to hand off the most recent trace-mode pathfinder
// GeoJSON to the hybrid-astar trace visualizer (public/docs/hybrid-astar-viz.html).
// Exported so SmartPathForm's "Open trace viz" button reads the same keys
// the builder writes.
export const TRACE_VIZ_BLOB_KEY = 'pathfinder-trace-last';
export const TRACE_VIZ_META_KEY = 'pathfinder-trace-last-meta';

class SmartPathBuilder {
  private inputVisitNavPoints: NavigationPoint[] = [];
  private addedZones: [Zone];

  constructor(private mission: Mission) {}

  async build(
    startLat: number,
    startLon: number,
    endLat: number,
    endLon: number,
    parameters: {
      preferredAngleDegrees: number;
      preferredAnglePenaltyFactor: number;
      gridCellSizeMeters: number;
      borderClusterPenaltyFactor: number;
      zoneClusterPenaltyFactor: number;
      minDistanceToBoundaryMeters: number;
      turnRadiusMeters: number;
      cruiseSpeedMps: number;
      turnSpeedFactor: number;
      sharpTurnWeight: number;
      dgpIterationCap: number;
      // Hybrid A* per-leg caps. Whichever trips first (node-expansion count
      // or wall-clock budget) ends the attempt and falls through to DGP.
      hybridAstarNodeCap: number;
      hybridAstarTimeBudgetMs: number;
      debugGeoJson: DebugGeoJsonMode;
    } = {
      preferredAngleDegrees: SMART_PATH_PREFERRED_ANGLE_DEGREES_DEFAULT,
      preferredAnglePenaltyFactor: SMART_PATH_PREFERRED_ANGLE_PENALTY_FACTOR_DEFAULT,
      gridCellSizeMeters: SMART_PATH_GRID_CELL_SIZE_METERS_DEFAULT,
      borderClusterPenaltyFactor: SMART_PATH_BORDER_CLUSTER_PENALTY_FACTOR_DEFAULT,
      zoneClusterPenaltyFactor: SMART_PATH_ZONE_CLUSTER_PENALTY_FACTOR_DEFAULT,
      minDistanceToBoundaryMeters: SMART_PATH_MIN_DISTANCE_TO_BOUNDARY_METERS_DEFAULT,
      turnRadiusMeters: 0,
      cruiseSpeedMps: SMART_PATH_CRUISE_SPEED_MPS_DEFAULT,
      turnSpeedFactor: SMART_PATH_TURN_SPEED_FACTOR_DEFAULT,
      sharpTurnWeight: SMART_PATH_SHARP_TURN_WEIGHT_DEFAULT,
      dgpIterationCap: SMART_PATH_DGP_ITERATION_CAP_DEFAULT,
      hybridAstarNodeCap: SMART_PATH_HYBRID_ASTAR_NODE_CAP_DEFAULT,
      hybridAstarTimeBudgetMs: SMART_PATH_HYBRID_ASTAR_TIME_BUDGET_MS_DEFAULT,
      debugGeoJson: 'off',
    },
  ): Promise<SmartPathBuildResult> {
    const originalBoundary = this.mission.getBoundary();

    if (!originalBoundary) {
      console.error('GridSampleReorderer::smartReorder() - field boundary not found');

      return {};
    }

    // Mission.getStartingPosition() falls back to the pull-in, which is (0, 0)
    // when no Starting/Parking/Pullin Point is placed. Checked before
    // generateInputNavPoints() mutates the mission (zone: deletes extra cores).
    if (isUnsetPosition(startLat, startLon)) {
      alertWarn('Smart Path needs a starting position: place a Starting Point, Parking Point or Pullin Point first.');

      return {};
    }

    this.generateInputNavPoints();

    // Pathfinder 500s on an empty tour ("No waypoints provided") — e.g. a zone
    // mission whose zones have no cores yet.
    if (this.inputVisitNavPoints.length === 0) {
      alertWarn('Smart Path has no pending cores to visit: place cores first (Auto-place Cores or Add Core).');

      return {};
    }

    const originalBoundaryMultiPolygon: Feature<MultiPolygon> = originalBoundary.toMultiPolygon();

    const navStartingPosition: NavigationPoint = {
      lat: startLat,
      lon: startLon,
      id: 'nav-starting-position',
      type: 'nav-starting-position',
    };

    const navEndPosition: NavigationPoint = {
      lat: endLat,
      lon: endLon,
      id: 'nav-end-position',
      type: 'nav-end-position',
    };

    // Make sure this is called with the original boundary, not a buffered one.
    this.assignBordersAndZones(navStartingPosition, navEndPosition, originalBoundaryMultiPolygon);

    let finalBoundary: Feature<Polygon> | undefined = undefined;

    try {
      finalBoundary = await this.prepareFinalBoundary(
        originalBoundaryMultiPolygon,
        startLat,
        startLon,
        endLat,
        endLon,
        parameters.minDistanceToBoundaryMeters,
      );
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'Unknown error';
      alertError(`Failed to prepare the boundary: ${errorMessage}`);

      return {};
    }

    if (!finalBoundary) {
      return {};
    }

    const {
      isValid,
      message: validationMessage,
      defectiveGeometries,
      warningGeometries,
    } = GeoDataUtilities.isPolygonValid(finalBoundary);

    if (!isValid) {
      alertError(
        `Boundary is not valid after buffering: ${validationMessage}. This may be caused by the boundary, bridges, or unsafe zones creating narrow areas that collapse when buffered.`,
      );

      return { routingBoundary: finalBoundary, defectiveGeometries, warningGeometries };
    }

    console.time('Building path');
    const {
      path,
      message,
      totalTimeSec,
      totalPhysicalDistance,
      totalBorderCrossings,
      totalZoneCrossings,
      costModel,
      requestWallTimeMs,
    } = await this.getOptimizedPath(
      navStartingPosition,
      navEndPosition,
      this.inputVisitNavPoints,
      finalBoundary,
      parameters,
    );
    console.timeEnd('Building path');

    if (!path) {
      alertWarn(`Failed to find optimal path: ${message}`);

      return { routingBoundary: finalBoundary };
    }

    return {
      path,
      routingBoundary: finalBoundary,
      totalTimeSec,
      totalPhysicalDistance,
      totalBorderCrossings,
      totalZoneCrossings,
      costModel,
      requestWallTimeMs,
    };
  }

  private generateInputNavPoints(): void {
    const pendingSamples = this.mission.getPendingSamples();

    if (this.mission.is_zone_mission()) {
      deleteExtraCoresForSamples(pendingSamples);

      this.inputVisitNavPoints = pendingSamples.reduce((acc: NavigationPoint[], cur: Sample) => {
        cur
          .getSoilCores()
          .filter((core) => core.isPending())
          .forEach((core) => {
            acc.push({
              id: core.name,
              lat: core.lat,
              lon: core.lon,
              type: 'nav-visit',
              sampleId: cur.sample_id,
            });
          });

        return acc;
      }, []);
    } else {
      // Grid missions: send first + last core per sample (instead of the
      // centroid). LKH keeps them contiguous via pathfinder's default
      // sameSamplePenaltyFactor (100); the robot drives a Dubin's segment
      // from first to last along the sample's core line — physically
      // meaningful entry/exit bearings.
      this.inputVisitNavPoints = pendingSamples.reduce((acc: NavigationPoint[], cur: Sample) => {
        const cores = cur
          .getSoilCores()
          .filter((core) => core.isPending())
          .sort((a, b) => a.core_number - b.core_number);

        if (cores.length === 0) {
          return acc;
        }

        if (cores.length === 1) {
          acc.push({
            id: cores[0].name,
            lat: cores[0].lat,
            lon: cores[0].lon,
            type: 'nav-visit',
            sampleId: cur.sample_id,
          });

          return acc;
        }

        // Multi-core sample: send first and last (the line endpoints).
        // For 2-core grid missions this is exactly both cores.
        const first = cores[0];
        const last = cores[cores.length - 1];
        acc.push({
          id: first.name,
          lat: first.lat,
          lon: first.lon,
          type: 'nav-visit',
          sampleId: cur.sample_id,
        });
        acc.push({
          id: last.name,
          lat: last.lat,
          lon: last.lon,
          type: 'nav-visit',
          sampleId: cur.sample_id,
        });

        return acc;
      }, []);
    }
  }

  private assignBordersAndZones(
    navStartingPosition: NavigationPoint,
    navEndPosition: NavigationPoint,
    boundaryMultiPolygon: Feature<MultiPolygon>,
  ) {
    const visitsPlustStartEnd = this.inputVisitNavPoints.concat([navStartingPosition, navEndPosition]);
    this.assignBorderIds(visitsPlustStartEnd, boundaryMultiPolygon);
    this.assignZoneIds(visitsPlustStartEnd, this.mission);
  }

  private async prepareFinalBoundary(
    boundaryMultiPolygon: Feature<MultiPolygon>,
    startLat: number,
    startLon: number,
    endLat: number,
    endLon: number,
    minDistanceToBoundaryMeters: number,
  ): Promise<Feature<Polygon> | undefined> {
    const manualyAddedBridges: Feature<MultiPolygon | Polygon>[] = this.mission
      .getPullinZones()
      .map((z) => z.toMultiPolygon());
    const unsafeZonesPoly = this.mission.getUnsafeZones().map((z) => z.toMultiPolygon());

    // Buffer the boundary inward and expand unsafe zones outward ONCE.
    // These cached results are reused when re-gluing for auto-bridges.
    const bufferWarnings: string[] = [];
    let bufferedBoundary: Feature<Polygon | MultiPolygon>;

    try {
      bufferedBoundary = bufferBoundaryPolygonsInward(
        boundaryMultiPolygon,
        minDistanceToBoundaryMeters,
        bufferWarnings,
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alertError(`Failed to buffer boundary: ${errorMessage}`);

      return undefined;
    }

    const expandedUnsafeZones = unsafeZonesPoly
      .map((uz) => expandPolygonOutward(uz, minDistanceToBoundaryMeters))
      .filter((uz): uz is Feature<Polygon | MultiPolygon> => uz !== null);

    // Glue buffered boundary + manual bridges - expanded unsafe zones
    let finalBoundary: Feature<Polygon> | undefined;

    try {
      finalBoundary = glueBoundaryWithBridgesAndUnsafeZones(bufferedBoundary, manualyAddedBridges, expandedUnsafeZones);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alertError(`Failed to glue boundary: ${errorMessage}`);

      return undefined;
    }

    const autoAddedBridges: Feature<MultiPolygon | Polygon>[] = [];

    // Every bridge the boundary ends up with, in the shape the Robot path
    // overlay draws and the warning below names (services/paths/bridges.ts).
    const bridgeRecords: SmartPathBridge[] = manualyAddedBridges.map((zone) => ({
      kind: 'manual' as const,
      geometry: zone.geometry,
    }));
    const rememberBridges = () => rememberSmartPathBridges(this.mission.instance_id, bridgeRecords);

    rememberBridges();

    const addBridge = (
      boundary: Feature<Polygon>,
      pointLat: number,
      pointLon: number,
      pointId: string,
    ): Feature<Polygon> | undefined => {
      const bridge: Feature<Polygon> = createBridgePolygonToBoundary([pointLon, pointLat], boundary);

      autoAddedBridges.push(bridge);
      bridgeRecords.push({ kind: 'auto', geometry: bridge.geometry, pointId });
      rememberBridges();

      dispatchMissionUpdated();

      // Re-glue from scratch using cached buffered boundary to avoid Martinez losing holes.
      try {
        return glueBoundaryWithBridgesAndUnsafeZones(
          bufferedBoundary,
          manualyAddedBridges.concat(autoAddedBridges),
          expandedUnsafeZones,
        );
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        alertError(`Failed to glue boundary: ${errorMessage}`);

        return undefined;
      }
    };

    // Since the boundary is already buffered inward, points just need to be inside
    // with a small margin rather than the full minDistanceToBoundaryMeters.
    const safeMargin = 0.5;

    const isStartPointSafeInside = isPointSafeInsidePolygon(finalBoundary, startLat, startLon, safeMargin);

    if (!isStartPointSafeInside) {
      alertWarn('Starting point is not safely inside the boundary (or too close to it)');

      finalBoundary = addBridge(finalBoundary, startLat, startLon, 'nav-starting-position');
    }

    if (!finalBoundary) {
      return undefined;
    }

    // Check endPoint after start bridge — they can be the same point
    // and we don't want to add another identical bridge.
    const isEndPointSafeInside = isPointSafeInsidePolygon(finalBoundary, endLat, endLon, safeMargin);

    if (!isEndPointSafeInside) {
      alertWarn('End point is not safely inside the boundary (or too close to it)');

      finalBoundary = addBridge(finalBoundary, endLat, endLon, 'nav-end-position');
    }

    if (!finalBoundary) {
      return undefined;
    }

    // Sometimes, our points (that the customer provided) are also too close to the boundary.
    // Make bridges for them too.
    for (const navPoint of this.inputVisitNavPoints) {
      const isPointInside = isPointSafeInsidePolygon(finalBoundary, navPoint.lat, navPoint.lon, safeMargin);

      if (!isPointInside) {
        finalBoundary = addBridge(finalBoundary, navPoint.lat, navPoint.lon, navPoint.id);
      }

      if (!finalBoundary) {
        return undefined;
      }
    }

    // One warning for the whole preparation: every bridge in the boundary - the
    // auto ones by the point they rescue (a point that was not safely inside, or
    // too close to the edge), the pull-in zones by count - and the buffering
    // issues. One message on purpose: the app shows a single warning at a time,
    // so a separate buffering alert would overwrite the bridge one.
    const notice = boundaryNotices(bridgeRecords, bufferWarnings);

    if (notice) {
      alertWarn(notice);
    }

    return finalBoundary;
  }

  private async getOptimizedPath(
    startPosition: NavigationPoint,
    endPosition: NavigationPoint,
    stops: NavigationPoint[],
    boundary: Feature<Polygon>,
    parameters: {
      preferredAngleDegrees: number;
      preferredAnglePenaltyFactor: number;
      gridCellSizeMeters: number;
      borderClusterPenaltyFactor: number;
      zoneClusterPenaltyFactor: number;
      minDistanceToBoundaryMeters: number;
      turnRadiusMeters: number;
      cruiseSpeedMps: number;
      turnSpeedFactor: number;
      sharpTurnWeight: number;
      dgpIterationCap: number;
      hybridAstarNodeCap: number;
      hybridAstarTimeBudgetMs: number;
      debugGeoJson: DebugGeoJsonMode;
    },
  ): Promise<{
    path: NavigationPoint[] | undefined;
    message: string;
    totalTimeSec?: number;
    totalPhysicalDistance?: number;
    totalBorderCrossings?: number;
    totalZoneCrossings?: number;
    costModel?: 'plain_atsp' | 'dubins_expanded';
    requestWallTimeMs?: number;
  }> {
    const {
      preferredAngleDegrees,
      preferredAnglePenaltyFactor,
      gridCellSizeMeters,
      borderClusterPenaltyFactor,
      zoneClusterPenaltyFactor,
      turnRadiusMeters,
      cruiseSpeedMps,
      turnSpeedFactor,
      sharpTurnWeight,
      dgpIterationCap,
      hybridAstarNodeCap,
      hybridAstarTimeBudgetMs,
      debugGeoJson,
    } = parameters;

    let response;
    const requestStartedAt = Date.now();

    try {
      // Append `?debug=1` when the user picked 'layer_with_trace'. The
      // Crow handler reads this query param and flips trace_enabled on the
      // GeoData, which causes the solver to capture per-leg attempts +
      // Hybrid A* sampled_nodes. Production (`'layer'`/`'download'`/`'off'`)
      // omits the param so the trace path is zero-overhead.
      const url =
        debugGeoJson === 'layer_with_trace' || debugGeoJson === 'download'
          ? // Both modes need the per-leg attempts[] (failed-Hybrid-A* traces in
            // particular). hybrid-astar-viz.html expects to find them on the
            // saved or dispatched GeoJSON FeatureCollection.
            `${pathfinderFindUrl()}?debug=1`
          : pathfinderFindUrl();

      response = await axios.post(
        url,
        {
          startPosition,
          endPosition,
          boundary: boundary.geometry,
          waypoints: stops.map((stop) => ({
            id: stop.id,
            lon: stop.lon,
            lat: stop.lat,
            borderClusterId: stop.borderClusterId,
            zoneClusterId: stop.zoneClusterId,
            sampleId: stop.sampleId,
          })),
          parameters: {
            missionType: pathfinderMissionType(this.mission),
            // Carbon-probe jobs export cores with command 3, which the robot's
            // is_c2c (mapper.h) excludes from the core-to-core path type.
            // Pathfinder mirrors that gate with this flag (defaults to false
            // when absent). Same predicate as to_ros so the two cannot drift.
            usesCarbonProbe: this.mission.usesCarbonProbe(),
            // The robot's arm-centred origin shift (field_param bit 2): the
            // GNSS target sits ~1 m short of every core along the approach
            // heading. Pathfinder's drive model runs with it, so send the
            // exact predicate to_ros_msg puts into field_param.
            implementCenteredNavigation: implementsCenteredNavigation(this.mission),
            preferredAngleDegrees,
            preferredAnglePenaltyFactor,
            gridCellSizeMeters,
            borderClusterPenaltyFactor,
            zoneClusterPenaltyFactor,
            // sameSamplePenaltyFactor keeps a sample's cores contiguous in
            // the LKH tour. Grid uses pathfinder's default (100). Zone
            // already enforces sample-level grouping via zoneClusterId,
            // and zone-interleave missions explicitly want cores from
            // different samples to interleave — so disable it for zones.
            sameSamplePenaltyFactor: this.mission.is_zone_mission() ? 0 : undefined,
            minDistanceToBoundaryMeters: 0,
            turnRadiusMeters,
            cruiseSpeedMps,
            turnSpeedFactor,
            sharpTurnWeight,
            dgpIterationCap,
            hybridAstarNodeCap,
            hybridAstarTimeBudgetMs,
            // 'off' is the only mode that DOESN'T want a debug payload.
            // The other three (download / layer / layer_with_trace) all
            // ask pathfinder to emit the debug GeoJSON.
            debugGeoJson: debugGeoJson !== 'off',
          },
        },
        {
          // Was: `debugGeoJson !== 'off' ? { responseType: 'arraybuffer' } : {}`.
          // undefined === axios' default ('json'), so the non-debug path is
          // unchanged.
          responseType: debugGeoJson !== 'off' ? 'arraybuffer' : undefined,
          // nginx in front of the on-prem box gates POST /path/find on this
          // header. Unset for local/Lambda, where it's simply ignored.
          headers: pathfinderHeaders(),
        },
      );
    } catch (error) {
      const elapsedSec = Math.round((Date.now() - requestStartedAt) / 1000);
      const errorMessage = `Error fetching path: ${extractPathfinderErrorDetail(error, elapsedSec)}`;
      await logger.log('SmartPathBuilder - error fetching path', error);
      console.error('SmartPathBuilder - error fetching path', error);

      return { path: undefined, message: errorMessage };
    }

    const routeData:
      | {
          routePaths: Route;
          totalBorderCrossings: number;
          totalZoneCrossings: number;
          totalCost: number;
          totalPhysicalDistance: number;
          totalTimeSec?: number;
          costModel?: 'plain_atsp' | 'dubins_expanded';
          requestWallTimeMs?: number;
        }
      | undefined = await this.parseServerResponse(response, debugGeoJson, this.mission.instance_id);

    if (!routeData) {
      return { path: undefined, message: 'Wrong data returned - no route data' };
    }

    const {
      routePaths: route,
      totalBorderCrossings,
      totalZoneCrossings,
      totalCost: _totalCost,
      totalPhysicalDistance: _totalPhysicalDistance,
    } = routeData;

    if (!Array.isArray(route)) {
      console.warn('SmartPathBuilder::getOptimizedPath - wrong data', route);

      return { path: undefined, message: 'Wrong data returned - route is not an array' };
    }

    if (!route.length) {
      console.warn('SmartPathBuilder::getOptimizedPath - empty route', route);

      return { path: undefined, message: 'Empty route returned' };
    }

    const navigationPoints: NavigationPoint[] = [];
    const routeLength = route.length;
    route.forEach((path: Path, pathIndex: number) => {
      const pathLength = path.length;

      path.forEach((navPoint: NavigationPoint, pointIndex) => {
        if (!this.shouldAddPoint(routeLength, pathLength, pathIndex, pointIndex)) {
          return;
        }

        // Do not enable this - to be discussed with Drew.
        // if (this.shouldSkipTooClosePoint(navPoint, boundary)) {
        //   return;
        // }

        navigationPoints.push(navPoint);
      });
    });

    return {
      path: navigationPoints,
      message: 'Success',
      totalTimeSec: routeData.totalTimeSec,
      totalPhysicalDistance: routeData.totalPhysicalDistance,
      totalBorderCrossings,
      totalZoneCrossings,
      costModel: routeData.costModel,
      requestWallTimeMs: routeData.requestWallTimeMs,
    };
  }

  private getNearestBoundaryPoint(
    navPoint: NavigationPoint,
    boundary: GeoJSONFeature<Polygon>,
  ): { point: Feature<GeoJSONPoint> | null; distance: number } {
    const pt = point([navPoint.lon, navPoint.lat]);

    // Convert boundary polygon (with holes) to a line collection
    const boundaryLines = polygonToLine(boundary);

    // If it's a FeatureCollection, find nearest point on *each* line, and keep the closest one
    let nearest: Feature<GeoJSONPoint> | null = null;
    let minDistance = Infinity;

    const features = boundaryLines.type === 'FeatureCollection' ? boundaryLines.features : [boundaryLines]; // If already a single Feature

    for (const line of features) {
      const candidate = nearestPointOnLine(line, pt, { units: 'meters' });
      const dist = distance(pt, candidate, { units: 'meters' });

      if (dist < minDistance) {
        nearest = candidate;
        minDistance = dist;
      }
    }

    return { point: nearest, distance: minDistance };
  }

  private isTooCloseToBoundary(distanceToBoundary: number): boolean {
    return distanceToBoundary < SMART_PATH_TOO_CLOSE_DISTANCE_TO_BOUNDARY_METERS;
  }

  private shouldSkipTooClosePoint(navPoint: NavigationPoint, boundary: Feature<Polygon>): boolean {
    // Never skip cores and samples.
    if (navPoint.type === 'nav-visit') {
      return false;
    }

    return this.isNavPointTooCloseToBoundary(navPoint, boundary);
  }

  private isNavPointTooCloseToBoundary(navPoint: NavigationPoint, boundary: Feature<Polygon>): boolean {
    const { distance } = this.getNearestBoundaryPoint(navPoint, boundary);

    return this.isTooCloseToBoundary(distance);
  }

  private shouldAddPoint(
    routeLength: number,
    pathLength: number,
    pathIndex: number,
    pointIndex: number,
  ): NavigationPointType | undefined {
    if (pathIndex === 0 && pointIndex === 0) {
      return 'nav-starting-position';
    } else if (pathIndex === routeLength - 1 && pointIndex === pathLength - 1) {
      return 'nav-end-position';
    } else if (pointIndex === pathLength - 1) {
      return 'nav-visit';
    } else if (pointIndex !== 0) {
      // do not add duplicate navigation points: each path starts with the point that the previous path ended with
      return 'nav-grid-point';
    }

    return undefined;
  }

  private adjustPointsAwayFromBoundary(path: NavigationPoint[], finalBoundary: Feature<Polygon>): NavigationPoint[] {
    return path.map((pt) => this.movePointAwayFromBoundary(pt, finalBoundary));
  }

  private movePointAwayFromBoundary(navPoint: NavigationPoint, boundary: Feature<Polygon>): NavigationPoint {
    const { point: nearest, distance } = this.getNearestBoundaryPoint(navPoint, boundary);

    const isTooClose = this.isTooCloseToBoundary(distance);

    if (!isTooClose) {
      return navPoint;
    }

    const pt = point([navPoint.lon, navPoint.lat]);

    const bearingAngle = bearing(nearest!, pt); // angle from boundary → point
    const moveDistance = SMART_PATH_TOO_CLOSE_DISTANCE_TO_BOUNDARY_METERS - distance + 0.01; // nudge a bit more
    const moved = destination(pt, moveDistance, bearingAngle, { units: 'meters' });

    return {
      ...navPoint,
      lon: moved.geometry.coordinates[0],
      lat: moved.geometry.coordinates[1],
    };
  }

  private generateDebugGeoJson(boundary: Feature<Polygon>, navPoints: NavigationPoint[] = []): FeatureCollection {
    const features: Feature[] = [];

    // Add full boundary polygon
    features.push({
      ...boundary,
      properties: { type: 'boundary-fill' },
    });

    const [outerRing, ...holes] = boundary.geometry.coordinates;

    // Outer ring as black line
    features.push(
      lineString(outerRing, {
        type: 'boundary-outer',
        stroke: '#000000',
        'stroke-width': 2,
      }),
    );

    // Holes as red lines
    holes.forEach((hole, i) => {
      features.push(
        lineString(hole, {
          type: 'boundary-hole',
          stroke: '#ff0000',
          'stroke-width': 2,
          name: `hole-${i}`,
        }),
      );
    });

    // Navigation path as a blue line
    const pathCoords = navPoints.map((pt) => [pt.lon, pt.lat]);

    if (pathCoords.length >= 2) {
      features.push(
        lineString(pathCoords, {
          type: 'nav-path',
          stroke: '#0000ff',
          'stroke-width': 3,
        }),
      );
    }

    // Navigation points as blue dots
    for (const pt of navPoints) {
      features.push(
        point([pt.lon, pt.lat], {
          type: 'nav-point',
          id: pt.id,
          'marker-color': '#0000ff',
          'marker-symbol': 'circle',
        }),
      );
    }

    return featureCollection(features) as FeatureCollection;
  }

  private async parseServerResponse(
    response: AxiosResponse<any, any>,
    mode: DebugGeoJsonMode,
    missionId: number,
  ): Promise<
    | {
        routePaths: Route;
        totalBorderCrossings: number;
        totalZoneCrossings: number;
        totalCost: number;
        totalPhysicalDistance: number;
        totalTimeSec?: number;
        costModel?: 'plain_atsp' | 'dubins_expanded';
      }
    | undefined
  > {
    const contentType = response.headers['content-type'];
    const boundaryMatch = contentType.match(/boundary=(.*)/);

    if (!boundaryMatch) {
      // Regular JSON response — nothing to fan out.
      return response.data;
    }

    const boundary = boundaryMatch[1];
    const buffer = new Uint8Array(response.data);
    const parts = splitMultipartBody(buffer, boundary);

    const jsonPart = parts.find((p) => p.headers['content-type']?.includes('application/json'));
    const gzipPart = parts.find((p) => p.headers['content-type']?.includes('application/gzip'));

    if (!jsonPart || !gzipPart) {
      alertError('Missing parts of server response');

      return;
    }

    const json = JSON.parse(new TextDecoder().decode(jsonPart.body));
    const decompressed = gunzipSync(gzipPart.body);
    const geojsonText = new TextDecoder().decode(decompressed);

    if (mode === 'download') {
      const blob = new Blob([geojsonText], { type: 'application/geo+json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'debug_cdt_and_route.geojson';
      a.click();
      URL.revokeObjectURL(url);
    } else if (mode === 'layer' || mode === 'layer_with_trace') {
      // Both modes render the debug GeoJSON as a MapMaker layer.
      const geojson = JSON.parse(geojsonText) as FeatureCollection;
      EventBus.dispatch(MISSION_EVENTS.DEBUG_GEOJSON_UPDATED, { geojson, missionId });

      // 'layer_with_trace' asked pathfinder to populate the top-level
      // `attempts` field on the FeatureCollection (one entry per leg, with
      // {step, outcome, reason, trace?} sub-records). Stash the whole
      // GeoJSON in localStorage so the technician can open it in the
      // hybrid-astar trace visualizer via the "Open trace viz" button in
      // SmartPathForm (rather than auto-attaching the data to `window`,
      // which leaked the parsed object into every page indefinitely).
      // The viz consumes and clears this entry on load.
      if (mode === 'layer_with_trace') {
        const attempts = (geojson as unknown as { attempts?: unknown }).attempts;

        if (Array.isArray(attempts)) {
          console.info(
            `[pathfinder] mission ${missionId}: ${attempts.length} legs traced. ` +
              `Use "Open trace viz" in SmartPathForm to inspect.`,
          );

          try {
            // Store the GZIPPED bytes (base64-encoded) instead of the raw
            // decompressed JSON text. Real missions decompress to 5-10 MB,
            // which overflows the ~5 MB localStorage quota; the gzip blob
            // is typically ~800 KB → ~1.1 MB after base64 — comfortably
            // under quota. Viz decompresses on read via DecompressionStream.
            let binary = '';

            for (let i = 0; i < gzipPart.body.length; i++) {
              binary += String.fromCharCode(gzipPart.body[i]);
            }

            const base64 = btoa(binary);
            localStorage.setItem(TRACE_VIZ_BLOB_KEY, base64);
            localStorage.setItem(
              TRACE_VIZ_META_KEY,
              JSON.stringify({
                missionId,
                ts: Date.now(),
                n_legs: attempts.length,
                encoding: 'gzip+base64',
              }),
            );
          } catch (e) {
            // Quota or btoa error. Fail soft so SmartPath itself still
            // completes.
            console.warn('[pathfinder] failed to stash trace GeoJSON in localStorage', e);
          }
        } else {
          console.warn(
            `[pathfinder] mission ${missionId}: layer_with_trace requested but no attempts[] field in response.`,
          );
        }
      }
    }

    return json;
  }

  private addNavPointToBorderCluster(
    out: Record<string, NavigationPoint[]>,
    borderClusterId: string,
    navPoint: NavigationPoint,
  ) {
    if (!out[borderClusterId]) {
      out[borderClusterId] = [];
    }

    out[borderClusterId].push(navPoint);

    navPoint.borderClusterId = borderClusterId;
  }

  private addNavPointToZoneCluster(
    out: Record<string, NavigationPoint[]>,
    zoneClusterId: string,
    navPoint: NavigationPoint,
  ) {
    if (!out[zoneClusterId]) {
      out[zoneClusterId] = [];
    }

    out[zoneClusterId].push(navPoint);

    navPoint.zoneClusterId = zoneClusterId;
  }

  private assignBorderIds(navPoints: NavigationPoint[], boundary: PolyOrMultiPoly): Record<number, NavigationPoint[]> {
    const out: Record<number, NavigationPoint[]> = {};

    if (boundary.geometry.type === 'Polygon') {
      navPoints.forEach((navPoint) => {
        const pt = point([navPoint.lon, navPoint.lat]);

        const borderClusterId: string = booleanPointInPolygon(pt, boundary)
          ? BORDER_CLUSTER_ID_DEFAULT.toString()
          : BORDER_CLUSTER_ID_OUTSIDE.toString();

        this.addNavPointToBorderCluster(out, borderClusterId, navPoint);
      });

      return out;
    }

    if (boundary.geometry.type === 'MultiPolygon') {
      navPoints.forEach((navPoint) => {
        const pt = point([navPoint.lon, navPoint.lat]);
        let borderClusterId: string = BORDER_CLUSTER_ID_OUTSIDE.toString();

        for (let i = 0; i < boundary.geometry.coordinates.length; i++) {
          const poly = polygon((boundary as GeoJSON.Feature<GeoJSON.MultiPolygon>).geometry.coordinates[i]);

          if (booleanPointInPolygon(pt, poly)) {
            borderClusterId = (BORDER_CLUSTER_ID_DEFAULT + i).toString();
          }
        }

        this.addNavPointToBorderCluster(out, borderClusterId, navPoint);
      });

      return out;
    }

    return out;
  }

  private assignZoneIds(navPoints: NavigationPoint[], mission: Mission): Record<number, NavigationPoint[]> {
    const out: Record<string, NavigationPoint[]> = {};

    navPoints.forEach((navPoint) => {
      const sampleZone = mission.getEnclosedSampleZone([navPoint.lat, navPoint.lon]);

      if (!sampleZone) {
        this.addNavPointToZoneCluster(out, ZONE_CLUSTER_ID_DEFAULT, navPoint);

        return;
      }

      const zone = sampleZone.getZone();

      if (!zone) {
        this.addNavPointToZoneCluster(out, ZONE_CLUSTER_ID_DEFAULT, navPoint);

        return;
      }

      this.addNavPointToZoneCluster(out, zone.zone_name, navPoint);
    });

    return out;
  }
}

export default SmartPathBuilder;
